import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { de, enUS, fr, hu } from "react-day-picker/locale";
import { Calendar } from "../../base/calendar/calendar";
import { formatIsoDate, localDate, parseIsoDate } from "./date-string";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
const DEFAULT_YEARS_SELECTABLE_IN_THE_PAST = 1;
const YEARS_SELECTABLE_IN_THE_FUTURE = 10;
const CALENDAR_LOCALES = {
    de,
    en: enUS,
    fr,
    hu
};
/**
 * Narrows any language tag (`i18n.resolvedLanguage`, a BCP-47 string) to a
 * calendar locale; anything the calendar has no names for reads English.
 * Derived from `CALENDAR_LOCALES`, so a locale added there is picked up here.
 */ export function datePickerLocaleFor(language) {
    const [subtag] = (language ?? "").toLowerCase().split("-");
    // An own-property check, not `in`: `in` walks the prototype, so a language
    // tag of `constructor` would answer true and hand the calendar a function.
    // (`Object.hasOwn` is ES2022; this package compiles against ES2021.)
    return Object.prototype.hasOwnProperty.call(CALENDAR_LOCALES, subtag) ? subtag : "en";
}
// react-day-picker zero-pads the week number (`01`) and leaves the column
// header empty, while the domain prints `KW1` — unpadded numbers plus a
// localized header keep the column recognizable as weeks, not days. Module
// constants rather than inline objects: the calendar keys its config memo on
// the identity of `formatters`, and a fresh one per render remounts the grid.
function weekNumberFormatters(header) {
    return {
        formatWeekNumber: (weekNumber)=>String(weekNumber),
        formatWeekNumberHeader: ()=>header
    };
}
const WEEK_NUMBER_FORMATTERS = {
    de: weekNumberFormatters("KW"),
    en: weekNumberFormatters("Wk"),
    fr: weekNumberFormatters("Sem."),
    hu: weekNumberFormatters("Hét")
};
export function DatePicker({ value, onValueCommit, locale, showWeekNumbers = false, yearsInPast = DEFAULT_YEARS_SELECTABLE_IN_THE_PAST, disabled = false, popupLabel, trigger, portalContainer }) {
    const [ownOpen, setOwnOpen] = React.useState(false);
    // Being disabled closes the popover for good: without dropping the open
    // state it would pop back open once the write that disabled it finishes.
    if (disabled && ownOpen) setOwnOpen(false);
    /*
   * Escape has to stop at the popup. Radix's dismissable layer — the thing a
   * dialog hosting this picker dismisses on — listens on the DOCUMENT in the
   * capture phase, so it reads the key before it ever reaches the calendar: one
   * press would close the popup AND the dialog around it. `window` is the only
   * place that captures earlier, so the key is taken there and the popup closes
   * itself. Nothing else on the page needs Escape while a calendar is open.
   */ React.useEffect(()=>{
        if (!ownOpen) return;
        const takeEscape = (event)=>{
            if (event.key !== "Escape") return;
            event.stopImmediatePropagation();
            setOwnOpen(false);
        };
        window.addEventListener("keydown", takeEscape, true);
        return ()=>window.removeEventListener("keydown", takeEscape, true);
    }, [
        ownOpen
    ]);
    const selected = value === null ? undefined : parseIsoDate(value);
    const currentYear = new Date().getFullYear();
    const firstNavigableMonth = localDate(currentYear - yearsInPast, 0, 1);
    const lastNavigableMonth = localDate(currentYear + YEARS_SELECTABLE_IN_THE_FUTURE, 11, 31);
    // A stored date outside the navigable range has to widen it: otherwise
    // react-day-picker clamps the opening month back into range and the stored
    // day is neither shown nor reachable through the year dropdown.
    const outsideNavigableRange = selected !== undefined && (selected < firstNavigableMonth || selected > lastNavigableMonth);
    const startMonth = selected && selected < firstNavigableMonth ? localDate(selected.getFullYear(), selected.getMonth(), 1) : firstNavigableMonth;
    const endMonth = selected && selected > lastNavigableMonth ? localDate(selected.getFullYear(), selected.getMonth() + 1, 0) : lastNavigableMonth;
    // A pick is a commit, and `required` keeps react-day-picker from ever firing
    // a deselect — so committing and cancelling stay mutually exclusive.
    function commitSelectedDay(day) {
        setOwnOpen(false);
        onValueCommit(formatIsoDate(day));
    }
    return /*#__PURE__*/ _jsxs(Popover, {
        open: ownOpen,
        onOpenChange: setOwnOpen,
        children: [
            /*#__PURE__*/ _jsx(PopoverTrigger, {
                render: trigger,
                disabled: disabled
            }),
            /*#__PURE__*/ _jsx(PopoverContent, {
                align: "start",
                "aria-label": popupLabel,
                className: "w-auto p-0",
                container: portalContainer,
                children: /*#__PURE__*/ _jsx(Calendar, {
                    mode: "single",
                    required: true,
                    selected: selected,
                    onSelect: commitSelectedDay,
                    defaultMonth: selected ?? new Date(),
                    locale: locale === undefined ? undefined : CALENDAR_LOCALES[locale],
                    ISOWeek: showWeekNumbers,
                    showWeekNumber: showWeekNumbers,
                    formatters: showWeekNumbers ? WEEK_NUMBER_FORMATTERS[locale ?? "en"] : undefined,
                    // The widened range spans centuries for a mistyped `0026-09-21`, and
                    // a year dropdown over it is one option per year — thousands of them,
                    // built on every open. So the caption is printed instead, which means
                    // this state has NO dropdowns and its month arrows are two millennia
                    // from anywhere useful: the way out is to clear the field and reopen,
                    // which starts the calendar on the current month with its dropdowns
                    // back. Showing the stored day at all is what the widening is for —
                    // the value has to be legible before it can be recognized as wrong.
                    captionLayout: outsideNavigableRange ? "label" : "dropdown",
                    startMonth: startMonth,
                    endMonth: endMonth
                })
            })
        ]
    });
}

//# sourceMappingURL=date-picker.js.map